import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip,
    Modal,
    Box,
    CircularProgress,
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import { useCurrency } from 'context/CurrencyContext';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection } from 'components/v2/record/DetailSection';

const CouponDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const { canDelete } = usePermission('coupons');
    const { formatCurrency } = useCurrency();

    const [coupon, setCoupon] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchCouponData();
    }, [id]);

    const fetchCouponData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/coupons/${id}`);
            setCoupon(response.data);
        } catch (error) {
            console.error('Error fetching coupon data:', error);
            setSnackbar({ open: true, message: 'Error loading coupon details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setCoupon(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/coupons/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Coupon updated successfully', severity: 'success' });
            fetchCouponData();
        } catch (error) {
            console.error('Error updating coupon:', error);
            setSnackbar({ open: true, message: 'Error updating coupon', severity: 'error' });
        }
    };

    const handleDeleteClick = () => {
        setDeleteModalOpen(true);
    };

    const handleDeleteCancel = () => {
        if (deleting) return;
        setDeleteModalOpen(false);
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/coupons/${id}`);
            setSnackbar({ open: true, message: 'Coupon deleted successfully', severity: 'success' });
            setDeleteModalOpen(false);
            setTimeout(() => navigate('/coupons'), 800);
        } catch (error) {
            console.error('Error deleting coupon:', error);
            const detail = error.response?.data?.detail;
            setSnackbar({
                open: true,
                message: typeof detail === 'string' ? detail : 'Error deleting coupon',
                severity: 'error',
            });
            setDeleting(false);
        }
    };

    const renderHyperlink = (text, onClick) => {
        if (!text || text === '-') {
            return '-';
        }
        return (
            <MDTypography
                component="span"
                onClick={onClick}
                variant="button"
                color="info"
                fontWeight="medium"
                sx={{
                    cursor: 'pointer',
                }}
            >
                {text}
            </MDTypography>
        );
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((field) => (
                    <Grid item xs={12} md={6} key={field.field}>
                        <InlineEditField
                            label={field.label}
                            value={coupon?.[field.field]}
                            options={field.options || null}
                            onSave={(newValue) => handleFieldUpdate(field.field, newValue)}
                            isEditing={isEditing}
                            readOnly={field.readOnly || false}
                            renderValue={field.renderValue || ((val) => (val ? String(val) : '-'))}
                        />
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
            </DashboardLayout>
        );
    }

    if (!coupon) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>Coupon not found</MDTypography>
                </MDBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        {coupon.name}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Coupon Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchCouponData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Coupon">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {canDelete && (
                                        <Tooltip title="Delete Coupon">
                                            <IconButton
                                                onClick={handleDeleteClick}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Delete sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Coupon Information", [
                                    { label: "Coupon Name", field: "name" },
                                    { label: "Code", field: "code" },
                                    {
                                        label: "Coupon Type",
                                        field: "coupon_type",
                                        readOnly: true,
                                        renderValue: (val) => val || 'General',
                                    },
                                    {
                                        label: "Discount Type",
                                        field: "discount_type",
                                        options: [{ value: "Fixed", label: "Fixed" }, { value: "Percent", label: "Percent" }]
                                    },
                                    { label: "Currency", field: "currency" },
                                    {
                                        label: "Value",
                                        field: "value",
                                        renderValue: (val) => {
                                            if (val == null || val === '') return '-';
                                            if (coupon.discount_type === 'Percent') return `${val}%`;
                                            return formatCurrency(val, coupon.currency);
                                        }
                                    },
                                    { label: "Available Count", field: "available_count" },
                                    { label: "Used Count", field: "used_count" },
                                    {
                                        label: "Activation Date",
                                        field: "activation_date",
                                        readOnly: true,
                                        renderValue: (val) => (val ? formatDate(val, 'date') : '-')
                                    },
                                    {
                                        label: "Expiration Date",
                                        field: "expiration_date",
                                        readOnly: true,
                                        renderValue: (val) => (val ? formatDate(val, 'date') : '-')
                                    },
                                ])}

                                {renderSection("Associated Package & Customer Details", [
                                    {
                                        label: "Package",
                                        field: "trip_package_id",
                                        readOnly: true,
                                        renderValue: () => {
                                            const title = coupon.trip_package?.title || (coupon.trip_package_id ? `Package #${coupon.trip_package_id}` : null);
                                            return title ? renderHyperlink(title, () => navigate(`/trip-packages/${coupon.trip_package_id}`)) : '-';
                                        }
                                    },
                                    {
                                        label: "Departure Date",
                                        field: "departure_date_id",
                                        readOnly: true,
                                        renderValue: () => {
                                            const depDate = coupon.departure_date?.departure_date
                                                ? formatDate(coupon.departure_date.departure_date, 'date')
                                                : coupon.departure_date?.name || (coupon.departure_date_id ? `Departure #${coupon.departure_date_id}` : null);
                                            const city = coupon.departure_date?.departure_city;
                                            const label = depDate ? (city ? `${depDate} (${city})` : depDate) : null;
                                            return label ? renderHyperlink(label, () => navigate(`/trip-departure-dates/${coupon.departure_date_id}`)) : '-';
                                        }
                                    },
                                    {
                                        label: "Customer",
                                        field: "customer_id",
                                        readOnly: true,
                                        renderValue: () => {
                                            const name = coupon.customer?.name || (coupon.customer_id ? `Account #${coupon.customer_id}` : null);
                                            return name ? renderHyperlink(name, () => navigate(`/accounts/${coupon.customer_id}`)) : '-';
                                        }
                                    },
                                    {
                                        label: "Trip Option",
                                        field: "option_id",
                                        readOnly: true,
                                        renderValue: () => {
                                            const title = coupon.option?.title || coupon.option?.name || (coupon.option_id ? `Option #${coupon.option_id}` : null);
                                            return title ? renderHyperlink(title, () => navigate(`/trip-options/${coupon.option_id}`)) : '-';
                                        }
                                    },
                                ])}

                                {renderSection("Audit Information", [
                                    {
                                        label: "Created By",
                                        field: "created_by_name",
                                        readOnly: true,
                                        renderValue: () => {
                                            const name = coupon.created_by_name || (coupon.created_by ? `User #${coupon.created_by}` : null);
                                            if (!name) return '-';
                                            if (coupon.created_by) {
                                                return renderHyperlink(name, () => navigate(`/users/${coupon.created_by}/profile`));
                                            }
                                            return name;
                                        }
                                    },
                                    {
                                        label: "Modified By",
                                        field: "updated_by_name",
                                        readOnly: true,
                                        renderValue: () => {
                                            const name = coupon.updated_by_name || (coupon.updated_by ? `User #${coupon.updated_by}` : null);
                                            if (!name) return '-';
                                            if (coupon.updated_by) {
                                                return renderHyperlink(name, () => navigate(`/users/${coupon.updated_by}/profile`));
                                            }
                                            return name;
                                        }
                                    },
                                    {
                                        label: "Created Date",
                                        field: "created_at",
                                        readOnly: true,
                                        renderValue: (val) => formatDate(val, 'datetime')
                                    },
                                    {
                                        label: "Modified Date",
                                        field: "updated_at",
                                        readOnly: true,
                                        renderValue: (val) => formatDate(val, 'datetime')
                                    },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* Delete Confirmation Modal */}
            <Modal
                open={deleteModalOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-coupon-modal"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', sm: 450 },
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <MDTypography variant="h6" component="h2" mb={2}>
                        Delete Coupon
                    </MDTypography>
                    <MDTypography variant="body2" color="text" mb={3}>
                        Are you sure you want to delete coupon <strong>{coupon.name}</strong> ({coupon.code})? This action cannot be undone.
                    </MDTypography>
                    <MDBox display="flex" justifyContent="flex-end" gap={2}>
                        <MDButton
                            onClick={handleDeleteCancel}
                            variant="outlined"
                            color="secondary"
                            disabled={deleting}
                        >
                            Cancel
                        </MDButton>
                        <MDButton
                            onClick={handleDeleteConfirm}
                            variant="gradient"
                            color="error"
                            disabled={deleting}
                        >
                            {deleting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                'Delete'
                            )}
                        </MDButton>
                    </MDBox>
                </Box>
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default CouponDetail;
